import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

export interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  permissions: string[];
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
  refreshUser: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3002';

// Helper: check if a JWT token is expired by decoding its payload
function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    // exp is in seconds, Date.now() is in ms
    return payload.exp * 1000 < Date.now();
  } catch {
    return true; // If we can't decode it, treat as expired
  }
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Initialize from localStorage, but validate token isn't expired
  const [user, setUser] = useState<User | null>(() => {
    try {
      const stored = localStorage.getItem('adminUser');
      const token = localStorage.getItem('adminToken');
      if (stored && token && !isTokenExpired(token)) {
        return JSON.parse(stored);
      }
      // Token is expired or missing - clean up
      if (token && isTokenExpired(token)) {
        localStorage.removeItem('adminUser');
        localStorage.removeItem('adminToken');
      }
      return null;
    } catch { return null; }
  });
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const token = localStorage.getItem('adminToken');
    const user = localStorage.getItem('adminUser');
    return !!(user && token && !isTokenExpired(token));
  });

  const logout = useCallback(() => {
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('adminUser');
    localStorage.removeItem('adminToken');
  }, []);

  useEffect(() => {
    // Validate stored token on app load
    const storedUser = localStorage.getItem('adminUser');
    const storedToken = localStorage.getItem('adminToken');
    
    if (storedUser && storedToken) {
      // Check if token is expired locally first
      if (isTokenExpired(storedToken)) {
        console.log('Stored token is expired, logging out');
        localStorage.removeItem('adminUser');
        localStorage.removeItem('adminToken');
        setUser(null);
        setIsAuthenticated(false);
        return;
      }

      try {
        const userData = JSON.parse(storedUser);
        setUser(userData);
        setIsAuthenticated(true);
      } catch (error) {
        localStorage.removeItem('adminUser');
        localStorage.removeItem('adminToken');
        setUser(null);
        setIsAuthenticated(false);
      }

      // Also verify with the server (non-blocking)
      fetch(`${API_BASE_URL}/api/auth/verify`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${storedToken}` },
      }).then(response => {
        if (response.status === 401) {
          console.log('Server rejected token, logging out');
          localStorage.removeItem('adminUser');
          localStorage.removeItem('adminToken');
          setUser(null);
          setIsAuthenticated(false);
        }
      }).catch(() => {
        // Network error - don't log out, just let the user continue with cached state
      });
    }

    // Listen for 401-triggered logouts from ApiService
    window.addEventListener('auth:logout', logout);
    return () => {
      window.removeEventListener('auth:logout', logout);
    };
  }, [logout]);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();

      if (data.access_token && data.user) {
        const userData: User = {
          id: data.user._id,
          email: data.user.email,
          name: data.user.name,
          role: data.user.role,
          permissions: data.user.permissions || [],
        };

        localStorage.setItem('adminToken', data.access_token);
        localStorage.setItem('adminUser', JSON.stringify(userData));
        setUser(userData);
        setIsAuthenticated(true);
        return true;
      }

      return false;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  const refreshUser = async () => {
    const storedUser = localStorage.getItem('adminUser');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setUser(userData);
      } catch (error) {
        console.error('Error refreshing user:', error);
      }
    }
  };

  const hasPermission = (permission: string): boolean => {
    // Admin roles have all normal permissions. Super-admin-only pages still use hasRole.
    if (user?.role === 'admin' || user?.role === 'super_admin') return true;
    return user?.permissions.includes(permission) || false;
  };

  const hasRole = (role: string): boolean => {
    return user?.role === role || false;
  };

  const value: AuthContextType = {
    user,
    login,
    logout,
    isAuthenticated,
    hasPermission,
    hasRole,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
